/**
 * @fileoverview useFilterGlobal - Global Filter State Management Hook
 *
 * Provides application-wide filter state management for geographic and temporal
 * filtering. Used across multiple modules for consistent filtering behavior.
 *
 * @module hooks/useFilterGlobal
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Date range configuration
 * @interface RangeDateTypes
 */
interface RangeDateTypes {
  /** Start date (format: YYYY-MM-DD) */
  initial_date?: string;
  /** End date (format: YYYY-MM-DD) */
  end_date?: string;
}

/**
 * Global filter context type
 * @interface FilterContextType
 */
interface FilterContextType {
  /** Geographic and temporal filters */
  filters: {
    /** Year filter options */
    year: any[];
    /** State/province filter options */
    state: any[];
    /** City filter options */
    city: any[];
    /** Neighborhood filter options */
    neighborhood: any[];
  };
  /** Function to update all filters at once */
  setFilters: (filters: FilterContextType['filters']) => void;
  /** Mentions filter value */
  filterMentions: string;
  /** Function to update mentions filter */
  setFilterMentions: (filter: string) => void;
  /** Date range filter */
  rangeDate: RangeDateTypes;
  /** Function to update date range */
  handleRangeDate: (date: RangeDateTypes) => void;
  /** Flag to trigger filter re-render */
  renderFilter: boolean;
  /** Function to toggle filter render flag */
  setRenderFilter: (value: boolean) => void;
}

/**
 * Context for global filter state
 * @internal
 */
const FilterGlobalContext = createContext<FilterContextType | undefined>(undefined);

/**
 * Provider component for global filter state.
 * Should wrap components that need access to global filtering.
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 *
 * @example
 * // In your app root or module wrapper
 * function AnalyticsModule() {
 *   return (
 *     <FilterGlobalProvider>
 *       <FilterBar />
 *       <AnalyticsDashboard />
 *     </FilterGlobalProvider>
 *   );
 * }
 */
export const FilterGlobalProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<FilterContextType['filters']>({
    year: [],
    state: [],
    city: [],
    neighborhood: [],
  });

  const [filterMentions, setFilterMentions] = useState('all');
  const [rangeDate, setRangeDate] = useState<RangeDateTypes>({});
  const [renderFilter, setRenderFilter] = useState(false);

  const handleRangeDate = (date: RangeDateTypes) => {
    setRangeDate(date);
  };

  return (
    <FilterGlobalContext.Provider
      value={{
        filters,
        setFilters,
        filterMentions,
        setFilterMentions,
        rangeDate,
        handleRangeDate,
        renderFilter,
        setRenderFilter,
      }}
    >
      {children}
    </FilterGlobalContext.Provider>
  );
};

/**
 * Hook to access global filter state and actions.
 *
 * Provides comprehensive filtering capabilities:
 * - Geographic filters (year, state, city, neighborhood)
 * - Temporal filters (date range)
 * - Mentions filter
 *
 * @returns {FilterContextType} Global filter state and actions
 * @returns {Object} returns.filters - Geographic filter state
 * @returns {Function} returns.setFilters - Update geographic filters
 * @returns {string} returns.filterMentions - Mentions filter value
 * @returns {Function} returns.setFilterMentions - Update mentions filter
 * @returns {RangeDateTypes} returns.rangeDate - Date range filter
 * @returns {Function} returns.handleRangeDate - Update date range
 * @returns {boolean} returns.renderFilter - Render trigger flag
 * @returns {Function} returns.setRenderFilter - Update render flag
 *
 * @throws {Error} If used outside of FilterGlobalProvider
 *
 * @example
 * // Using geographic filters
 * function CityFilter() {
 *   const { filters, setFilters } = useFilterGlobal();
 *
 *   const handleCityChange = (cities: string[]) => {
 *     setFilters({ ...filters, city: cities });
 *   };
 *
 *   return (
 *     <MultiSelect
 *       options={cityOptions}
 *       value={filters.city}
 *       onChange={handleCityChange}
 *     />
 *   );
 * }
 *
 * @example
 * // Using date range
 * function DateRangeFilter() {
 *   const { rangeDate, handleRangeDate } = useFilterGlobal();
 *
 *   return (
 *     <DateRangePicker
 *       startDate={rangeDate.initial_date}
 *       endDate={rangeDate.end_date}
 *       onChange={(start, end) => handleRangeDate({
 *         initial_date: start,
 *         end_date: end,
 *       })}
 *     />
 *   );
 * }
 */
export const useFilterGlobal = (): FilterContextType => {
  const context = useContext(FilterGlobalContext);
  if (!context) {
    throw new Error('useFilterGlobal must be used within a FilterGlobalProvider');
  }
  return context;
};
